import { HttpClient, HttpParams, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';

import {
  IServerSideGetRowsRequest,
  LoadSuccessParams,
} from 'ag-grid-community';

import {
  CLIENT_ADMIN_PATCH_PLATFORM_IDENTITY,
  CLIENT_ADMIN_POST_COMPANIES,
  CLIENT_ADMIN_POST_PLATFORM_IDENTITIES,
  CLIENT_ADMIN_POST_SCHEDULES,
  CLIENT_ADMIN_POST_ROLES,
  CLIENT_ADMIN_POST_USER_ACTION_STATS_DAILY_ACTIVE_USERS,
  CLIENT_ADMIN_POST_USER_ACTION_STATS_ERRORS_PER_DAY,
  CLIENT_ADMIN_POST_USER_ACTION_STATS_TOP_ACTIVE_COMPANIES,
  CLIENT_ADMIN_POST_USER_ACTION_LOGS,
  CLIENT_ADMIN_POST_USER_GROUPS,
  CLIENT_ADMIN_POST_USERS,
} from '../endpoints/client-admin.endpoints';

import { toQueryParams } from '../api-utils';
import { UtilsService } from 'src/app/core/services/utils.service';

import { Observable, map, tap } from 'rxjs';

export interface UserActionStatsRangeParams {
  from?: string | number;
  to?: string | number;
  gap?: string;
}

export interface UserActionStatsSeries {
  name: string;
  data: number[];
  company_id?: string;
  total?: number;
}

export interface UserActionStatsResponse {
  from: string;
  to: string;
  gap: string;
  limit?: number;
  categories: string[];
  series: UserActionStatsSeries[];
}

// TODO: We will pass typized objects to the services containing all inputs
@Injectable({ providedIn: 'root' })
export class ClientAdminApiService {
  private readonly http = inject(HttpClient);
  private readonly utils = inject(UtilsService);

  getRolesAgGridModel(gridParams: any): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(
      CLIENT_ADMIN_POST_ROLES,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  getUserGroupsAgGridModel(gridParams: any): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(
      CLIENT_ADMIN_POST_USER_GROUPS,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  getCompaniesAgGridModel(
    gridParams: any,
    output: 'table' | 'csv',
  ): Observable<any> {
    const httpParams = toQueryParams({ output });

    if (output === 'csv') {
      return this.http.post(CLIENT_ADMIN_POST_COMPANIES, gridParams, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      });
    }

    // normal grid JSON
    return this.http.post<any>(CLIENT_ADMIN_POST_COMPANIES, gridParams, {
      params: httpParams,
    });
  }

  getSchedulesAgGridModel(
    gridParams: any,
    output: 'table' | 'csv',
  ): Observable<any> {
    const httpParams = toQueryParams({ output });

    if (output === 'csv') {
      return this.http.post(CLIENT_ADMIN_POST_SCHEDULES, gridParams, {
        params: httpParams,
        observe: 'response',
        responseType: 'blob',
      });
    }

    // normal grid JSON
    return this.http.post<any>(CLIENT_ADMIN_POST_SCHEDULES, gridParams, {
      params: httpParams,
    });
  }

  getUsersAgGridModel(
    gridParams: any,
    companyId?: string,
  ): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({
      output: 'table',
      company_id: companyId || undefined,
    });

    return this.http.post<LoadSuccessParams>(
      CLIENT_ADMIN_POST_USERS,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  /**
   * POST /client-admin/users?output=csv - server-side streaming of the WHOLE
   * filtered resultset (the client-side CSV covers only the rows cached by
   * the server-side row model). Triggers the browser download and completes.
   */
  exportUsersCsv(
    request: IServerSideGetRowsRequest,
    companyId?: string,
  ): Observable<void> {
    return this.http
      .post(CLIENT_ADMIN_POST_USERS, request, {
        params: toQueryParams({
          output: 'csv',
          company_id: companyId || undefined,
        }),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const fileName = this.utils.getFileNameFromDisposition(
            response.headers.get('content-disposition'),
            'users.csv',
          );
          this.utils.downloadBlob(response.body!, fileName);
        }),
        map(() => undefined),
      );
  }

  getPlatformIdentitiesAgGridModel(
    gridParams: any,
  ): Observable<LoadSuccessParams> {
    const httpParams = toQueryParams({ output: 'table' });

    return this.http.post<LoadSuccessParams>(
      CLIENT_ADMIN_POST_PLATFORM_IDENTITIES,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  /** POST /client-admin/platform_identities?output=csv - see exportUsersCsv(). */
  exportPlatformIdentitiesCsv(
    request: IServerSideGetRowsRequest,
  ): Observable<void> {
    return this.http
      .post(CLIENT_ADMIN_POST_PLATFORM_IDENTITIES, request, {
        params: toQueryParams({ output: 'csv' }),
        observe: 'response',
        responseType: 'blob',
      })
      .pipe(
        tap((response: HttpResponse<Blob>) => {
          const fileName = this.utils.getFileNameFromDisposition(
            response.headers.get('content-disposition'),
            'platform-identities.csv',
          );
          this.utils.downloadBlob(response.body!, fileName);
        }),
        map(() => undefined),
      );
  }

  updatePlatformIdentity(platformIdentity: any): Observable<any> {
    return this.http.patch<any>(
      CLIENT_ADMIN_PATCH_PLATFORM_IDENTITY(platformIdentity.id),
      { platform_identity: platformIdentity },
    );
  }

  markPlatformIdentityAsSpammer(id: number): Observable<any> {
    return this.updatePlatformIdentity({ id, is_spammer: true });
  }

  getUserActivityLogsAgGridModel(
    gridParams: IServerSideGetRowsRequest,
    userId: string = null,
    objectId: string = null,
    object_class_id: number = null,
    activeCompanyId?: string,
  ): Observable<LoadSuccessParams | any> {
    // Hand-rolled, not toQueryParams: userId/objectId are camelCase legacy
    // wire keys, and `if (value)` deliberately skips ''/0 (api-layer.md).
    let httpParams = new HttpParams().set('output', 'table');

    const optionalParams: Record<string, any> = {
      userId,
      objectId,
      object_class_id,
      requested_company_id: activeCompanyId,
    };

    for (const [key, value] of Object.entries(optionalParams)) {
      if (value) httpParams = httpParams.set(key, value);
    }

    return this.http.post<LoadSuccessParams>(
      CLIENT_ADMIN_POST_USER_ACTION_LOGS,
      gridParams,
      {
        params: httpParams,
      },
    );
  }

  getUserActionStatsDailyActiveUsers(
    range: UserActionStatsRangeParams = {},
  ): Observable<UserActionStatsResponse> {
    return this.http.post<UserActionStatsResponse>(
      CLIENT_ADMIN_POST_USER_ACTION_STATS_DAILY_ACTIVE_USERS,
      range,
    );
  }

  getUserActionStatsTopActiveCompanies(
    range: UserActionStatsRangeParams & { limit?: number } = {},
  ): Observable<UserActionStatsResponse> {
    return this.http.post<UserActionStatsResponse>(
      CLIENT_ADMIN_POST_USER_ACTION_STATS_TOP_ACTIVE_COMPANIES,
      range,
    );
  }

  getUserActionStatsErrorsPerDay(
    range: UserActionStatsRangeParams = {},
  ): Observable<UserActionStatsResponse> {
    return this.http.post<UserActionStatsResponse>(
      CLIENT_ADMIN_POST_USER_ACTION_STATS_ERRORS_PER_DAY,
      range,
    );
  }
}
